import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Trash2, UserPlus, Shield } from 'lucide-react';

const AdminSettings = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newUser, setNewUser] = useState({ username: '', password: '' });
    const [message, setMessage] = useState('');

    const fetchUsers = async () => {
        try {
            const { data } = await api.get('/users');
            setUsers(data);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            await api.post('/users', newUser);
            setMessage('Admin added successfully');
            setNewUser({ username: '', password: '' });
            fetchUsers();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage(error.response?.data?.message || 'Error adding admin');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to remove this admin?')) {
            try {
                await api.delete(`/users/${id}`);
                fetchUsers();
            } catch (error) {
                console.error(error);
            }
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div>
                <h1 className="text-2xl font-bold mb-4">Admin Management</h1>
                <p className="text-gray-500 mb-6">Manage administrators who have access to this dashboard.</p>

                {message && <div className={`p-3 rounded mb-4 ${message.includes('success') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{message}</div>}

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
                    <h2 className="text-lg font-semibold mb-4 flex items-center"><UserPlus className="w-5 h-5 mr-2" /> Add New Admin</h2>
                    <form onSubmit={handleAddUser} className="flex gap-4 items-end">
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-1">Username</label>
                            <input
                                type="text"
                                value={newUser.username}
                                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                className="w-full p-2 border rounded bg-transparent dark:border-gray-600"
                                required
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-1">Password</label>
                            <input
                                type="password"
                                value={newUser.password}
                                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                className="w-full p-2 border rounded bg-transparent dark:border-gray-600"
                                required
                            />
                        </div>
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add Admin</button>
                    </form>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="p-4 font-medium">Username</th>
                                <th className="p-4 font-medium">Role</th>
                                <th className="p-4 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {users.map(user => (
                                <tr key={user._id}>
                                    <td className="p-4 flex items-center">
                                        <Shield className="w-4 h-4 mr-2 text-blue-500" />
                                        {user.username}
                                    </td>
                                    <td className="p-4 text-sm text-gray-500 dark:text-gray-400 capitalize">{user.role}</td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => handleDelete(user._id)} className="text-red-500 hover:text-red-700 ml-4">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminSettings;
